import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { ArrowLeft, MapPin, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SponsorProfile() {
  const { sponsorId } = useParams();
  const navigate = useNavigate();
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/sponsors/${sponsorId}/public-profile`);
        setSponsor(res.data);
      } catch {
        setSponsor(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sponsorId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  if (!sponsor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Sponsor not found.</p>
        <button onClick={() => navigate(-1)} className="mt-3 text-sm font-medium" style={{ color: 'var(--lk-sage)' }}>Go back</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:opacity-70" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Sponsor</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Sponsor card */}
        <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          {/* Logo placeholder */}
          {sponsor.logo_url ? (
            <img src={sponsor.logo_url} alt={sponsor.name} className="w-16 h-16 rounded-xl object-cover mb-4" />
          ) : (
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--lk-soft-clay, #C9A88A)', opacity: 0.3 }}>
              <span className="text-2xl font-bold" style={{ color: 'white' }}>{sponsor.name?.[0] || 'S'}</span>
            </div>
          )}

          {/* Name */}
          <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }} data-testid="sponsor-name">
            {sponsor.name}
          </h2>

          {/* Location */}
          {sponsor.location && (
            <div className="flex items-center gap-1 mb-4">
              <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--fy-text-disabled)' }} />
              <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{sponsor.location}</span>
            </div>
          )}

          {/* Description */}
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)', lineHeight: 1.6 }}>
            {sponsor.description}
          </p>

          {/* Message */}
          {sponsor.message && (
            <p className="text-xs italic" style={{ color: 'var(--fy-text-disabled)' }} data-testid="sponsor-message">
              {sponsor.message}
            </p>
          )}
        </Card>

        {/* External link — secondary, bottom */}
        {sponsor.website && (
          <div className="mt-6 text-center">
            <a
              href={sponsor.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium"
              style={{ color: 'var(--fy-text-secondary)' }}
              data-testid="sponsor-website"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Visit website
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
